// Centralized, typed access to import.meta.env — the rest of the app
// never touches import.meta.env directly, so a missing var fails
// loudly at startup instead of surfacing as `undefined` deep in a request.
function getEnvVar(key: string, value: string | undefined, fallback?: string): string {
  if (value) return value;
  if (fallback !== undefined) return fallback;
  throw new Error(`Missing required env var: ${key}`);
}

const apiBaseUrl = getEnvVar(
  'VITE_API_BASE_URL',
  import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL,
  import.meta.env.DEV ? 'http://localhost:8000/api' : 'https://vtdemo.in/dms/backend/public/api'
);

// Derive backend root URL by stripping trailing /api or /api/
const backendBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '');

export const env = {
  apiBaseUrl,
  backendBaseUrl,
  wsUrl: import.meta.env.VITE_WS_URL || (import.meta.env.DEV ? 'ws://localhost:6001' : ''),
  maxUploadSizeMb: Number(import.meta.env.VITE_MAX_UPLOAD_SIZE_MB ?? 200),
  isDev: import.meta.env.DEV,
} as const;

/**
 * Resolves a storage or asset path against the backend base URL.
 * Works seamlessly across both local development and production.
 *
 * Examples:
 * - "/storage/settings/logo.png" -> "https://vtdemo.in/dms/backend/public/storage/settings/logo.png" (in prod)
 * - "storage/settings/logo.png"  -> "https://vtdemo.in/dms/backend/public/storage/settings/logo.png" (in prod)
 * - "settings/logo.png"          -> "https://vtdemo.in/dms/backend/public/storage/settings/logo.png" (in prod)
 * - "https://..." or "blob:..."  -> returned as-is
 */
export function getStorageUrl(path?: string | null): string {
  if (!path) return '';

  const trimmed = path.trim();
  if (!trimmed) return '';

  // Already an absolute URL, protocol-relative, blob URL, or data URI
  if (/^(https?:|\/\/|blob:|data:)/i.test(trimmed)) {
    return trimmed;
  }

  const base = env.backendBaseUrl;

  if (trimmed.startsWith('/storage/')) {
    return `${base}${trimmed}`;
  }

  if (trimmed.startsWith('storage/')) {
    return `${base}/${trimmed}`;
  }

  if (trimmed.startsWith('/')) {
    return `${base}${trimmed}`;
  }

  return `${base}/storage/${trimmed}`;
}
